import axios, { type InternalAxiosRequestConfig } from 'axios';
import type { UnlinkedPayload } from './types';
import { keycloak } from '../auth/keycloak';
import { queryClient } from '../providers/QueryProvider';

// In dev with no VITE_API_URL set, use empty base so requests go through the Vite
// proxy (relative /api -> http://localhost:3001). In production, VITE_API_URL
// should point to the actual backend origin.
export const API_BASE = import.meta.env.VITE_API_URL ?? '';

const apiClient = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Reads the `USER_NOT_LINKED` rejection payload emitted by the backend
 * JwtAuthGuard.
 *
 * This is a pure read of a query error on purpose. The payload used to be cached
 * in a module-level flag that outlived the condition it described: once set, it
 * was never cleared, so a user who got provisioned mid-session kept seeing the
 * "not linked" page even after `/api/me` had started answering 200. Deriving it
 * from the live query result makes the gate lift by itself.
 */
export function getUnlinkedPayload(error: unknown): UnlinkedPayload | null {
  if (!axios.isAxiosError(error)) return null;
  if (error.response?.status !== 403) return null;

  const data = error.response.data as Partial<UnlinkedPayload> | undefined;
  if (data?.error !== 'USER_NOT_LINKED') return null;

  return data as UnlinkedPayload;
}

export function setAccessToken(token: string | undefined): void {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common.Authorization;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error) && error.response) {
      if (error.response.status === 401) {
        const originalRequest = error.config as
          (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

        // TePresta pattern: on 401 ALWAYS force a token refresh — the server
        // rejection is the source of truth, not isTokenExpired(). Retry once
        // with the fresh token; if that fails, the session is invalid.
        if (originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshed = await keycloak.updateToken(-1);
            if (refreshed && keycloak.token) {
              setAccessToken(keycloak.token);
              originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
              // Refetch permissions/user data with the fresh token so a single
              // stale-token failure does not leave the UI broken until reload.
              queryClient.invalidateQueries({ queryKey: ['me'] });
              return apiClient(originalRequest);
            }
          } catch {
            // refresh token revoked/invalid — fall through
          }
        }
        // No valid session left: emit event so the app closes the Keycloak
        // session instead of looping back to a login redirect.
        window.dispatchEvent(new CustomEvent('aid:session-expired'));
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
